import { useContext } from "react"

import Skeleton from "react-loading-skeleton"
import { useSelector } from "react-redux"

import { SvgIcon } from "@components/common"
import { NewTeeupPageContext } from "@contexts/NewTeeupPageContext"
import { selectActiveTeeupSettings } from "@selectors/activeTeeup"
import { TEEUP_SETTINGS_TYPES } from "constants/teeups"

import ActionButton from "../ActionButton"

import "./index.scss"

import AskPeopleModal from "../Modals/AskPeople"
import NudgeModal from "../Modals/Nudge"

// People Card Detail-Groups spec (Notion d45923fee2264c559a5512601920958c, 2026-07-05): this
// row now only ever renders the People card's own collapsed/expanded ACTION buttons (a compact
// Invite icon pill while collapsed -- no "Change Status", see PEOPLE-ROW-PILLS below;
// Nudge/Invite/Manage Participants while expanded). The `isParticipants` avatar-summary variant
// this component used to also render
// (for the old nested "Participants" sub-collapse) is gone -- that sub-collapse no longer
// exists (Content/Participants/index.jsx is now a plain "Info" detail group, no toggle of its
// own, no avatar-only summary state to render here). Removing it also removes the second place
// the participant avatar stack could render, which is part of QJ's defect-1 duplication fix.
const SubTitleContent = ({ people, isCollapsed, isOrganizer, currentUser }) => {
  const settings = useSelector(selectActiveTeeupSettings)

  const { setIsInvitePeopleOpen, setSettingsType, setIsSettings } =
    useContext(NewTeeupPageContext)

  const handleInviteClick = () => {
    setIsInvitePeopleOpen(true)
  }

  const handleManageParticipantsClick = () => {
    setIsSettings(true)
    setSettingsType(TEEUP_SETTINGS_TYPES.PARTICIPANTS)
  }

  if (people.length === 0)
    return (
      <div className="people-sup-skeleton">
        <Skeleton width="100%" borderRadius={50} />
      </div>
    )

  return (
    <div className="people-sub-container">
      {/* Spec ("People Card Collapsed", 5ba3f8cbdf4c43f4a20d3bc91beea892): "Add Participants"
          and "Change Status" collapsed action buttons, present while the card is COLLAPSED (the
          `isCollapsed` store flag is confusingly named -- false is this component's actual
          collapsed state; see Content/Participants/index.jsx for the full explanation). This is
          ADDITIONAL to the pre-existing Invite/Manage-Participants row a few lines down (which
          instead shows once the card is EXPANDED, isCollapsed===true). */}
      {/* PEOPLE-ROW-PILLS 2026-07-05 (QJ):
          1. "Change Status" removed entirely -- the "I'm going" attendance control already on
             the People row (TitleContent, right side) serves this exact function; a second
             control here was a redundant duplicate, not a distinct feature.
          2. "Add Participants" collapsed to a small icon pill labeled "Invite" (symbol-first,
             product language) -- same click behavior (handleInviteClick / invite flow), just
             compact. Uses the dedicated person-plus glyph (`invite.svg`) instead of the
             generic "people-2" roster icon so it doesn't visually double with Info/roster
             iconography elsewhere on the card. */}
      {!isCollapsed && (
        <div className="people-collapsed-actions">
          <ActionButton
            onClick={handleInviteClick}
            isDisabled={!isOrganizer && !settings?.allowInviteOthers}
            className="people-attendance-button people-invite-pill"
            data-testid="teeup-detail-invite-pill">
            <SvgIcon name="invite" color="var(--gray4)" />
            Invite
          </ActionButton>
        </div>
      )}

      {isCollapsed && (
        <div className="people-actions">
          <NudgeModal />
          <AskPeopleModal people={people} />

          <ActionButton
            onClick={handleInviteClick}
            isDisabled={!isOrganizer && !settings?.allowInviteOthers}
            className="people-attendance-button">
            <SvgIcon name="people-2" color="var(--gray4)" />
            Invite
          </ActionButton>

          {isOrganizer && (
            <ActionButton
              onClick={handleManageParticipantsClick}
              className="people-attendance-button">
              <SvgIcon name="people-manage" color="var(--gray4)" />
              Manage Participants
            </ActionButton>
          )}
        </div>
      )}
    </div>
  )
}

export default SubTitleContent
